/**
 * Purpose: Long text that clamps to N lines and reveals the rest IN PLACE with a
 *          smooth height animation. Generic design-system primitive — usable for
 *          business/hotel/mosque descriptions, review bodies, announcements.
 * Inputs: children (the text), lines (clamp limit, default 2), moreLabel,
 *         lessLabel, className (applied to the text element).
 * Outputs: clamped text + a See more / See less toggle that appears ONLY when the
 *          text actually overflows the clamp.
 *
 * How the animation works: line-clamp itself cannot be transitioned, so the text
 * sits in a wrapper whose max-height IS animatable. Collapsed = the clamped
 * height; expanded = the full scroll height, then max-height is released to
 * 'none' so later reflows (resize, font load) are never clipped. Overflow is
 * re-measured on resize because the clamp depends on the available width.
 *
 * Reduced motion is honoured (prefers-reduced-motion → no transition).
 */
import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

const CLAMP_CLASS = {
  1: 'line-clamp-1',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
};

const DURATION_MS = 250;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const ExpandableText = ({
  children,
  lines = 2,
  moreLabel = 'See more',
  lessLabel = 'See less',
  className = '',
}) => {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const [maxHeight, setMaxHeight] = useState(null); // null = 'none' (no clipping)
  const textRef = useRef(null);
  const bodyId = useId();

  const measure = useCallback(() => {
    const el = textRef.current;
    // Only meaningful while clamped: expanded text is its own full height.
    if (!el || expanded) return;
    setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [expanded]);

  useLayoutEffect(measure, [measure, children]);

  useEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  const toggle = useCallback(() => {
    const el = textRef.current;
    if (!el) {
      setExpanded((prev) => !prev);
      return;
    }

    const from = el.clientHeight; // current (clamped or full) height
    const next = !expanded;

    if (prefersReducedMotion()) {
      setExpanded(next);
      setMaxHeight(null);
      return;
    }

    // Pin the wrapper to the current height, flip the clamp, then animate to the
    // new height on the next frame — so the browser has two heights to tween.
    setMaxHeight(from);
    setExpanded(next);

    requestAnimationFrame(() => {
      const node = textRef.current;
      if (!node) return;
      // scrollHeight is the unclamped height when expanding; when collapsing the
      // clamp is already applied, so clientHeight is the target.
      const to = next ? node.scrollHeight : node.clientHeight;
      setMaxHeight(to);
      window.setTimeout(() => {
        // Release the cap once the tween is done: a fixed max-height would clip
        // the text if it later reflows (window resize, late font load).
        if (next) setMaxHeight(null);
      }, DURATION_MS);
    });
  }, [expanded]);

  const clampClass = CLAMP_CLASS[lines] || CLAMP_CLASS[2];

  return (
    <div>
      <div
        className="overflow-hidden transition-[max-height] duration-[250ms] ease-out motion-reduce:transition-none"
        style={{ maxHeight: maxHeight == null ? undefined : `${maxHeight}px` }}
      >
        <p
          ref={textRef}
          id={bodyId}
          className={[className, expanded ? '' : clampClass].filter(Boolean).join(' ')}
        >
          {children}
        </p>
      </div>

      {(overflowing || expanded) && (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={expanded}
          aria-controls={bodyId}
          className="mt-1 rounded text-xs font-semibold text-brand-700 hover:text-brand-800 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-brand-400 dark:hover:text-brand-300"
        >
          {expanded ? lessLabel : moreLabel}
        </button>
      )}
    </div>
  );
};

export default ExpandableText;
